/**
 * ThreatIntelPanel.tsx — Unified Threat Intelligence status panel.
 *
 * Shows one row per source: name, last updated, status badge, version/date.
 * "Check Now" triggers POST /api/v1/threat-intel/check-now.
 */

import React, { useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import BugReportIcon from '@mui/icons-material/BugReport';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShieldIcon from '@mui/icons-material/Shield';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import UpdateIcon from '@mui/icons-material/Update';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import axios from 'axios';
import API_BASE from '../config/api';

// ── Types ──────────────────────────────────────────────────────────────────────

interface SourceStatus {
  source: string;
  status: 'success' | 'failed' | 'never_run' | 'no_change';
  summary: string;
  version_or_date: string | null;
  last_updated: string | null;
  schedule: 'daily' | 'weekly';
}

// ── Source display metadata ────────────────────────────────────────────────────

const SOURCE_META: Record<string, { label: string; icon: React.ReactNode; desc: string }> = {
  prowler:          { label: 'Prowler',            icon: <UpdateIcon fontSize="small" />,       desc: 'GitHub releases' },
  cve_nvd:          { label: 'CVE / NVD',          icon: <BugReportIcon fontSize="small" />,    desc: 'NIST NVD last 7 days' },
  cisa_kev:         { label: 'CISA KEV',           icon: <WarningAmberIcon fontSize="small" />, desc: 'Known exploited vulns' },
  mitre_attack:     { label: 'MITRE ATT&CK',       icon: <ShieldIcon fontSize="small" />,       desc: 'GitHub STIX releases' },
  ai_tools:         { label: 'AI Tools',           icon: <SmartToyIcon fontSize="small" />,     desc: 'ai-collection + seed' },
  ai_provider_ips:  { label: 'AI Provider IPs',    icon: <SmartToyIcon fontSize="small" />,     desc: 'DNS-resolved IP ranges' },
  compliance_rss:   { label: 'Compliance RSS',     icon: <ShieldIcon fontSize="small" />,       desc: 'SOC2/NIST/GDPR/HIPAA feeds' },
};

// ── Status chip ────────────────────────────────────────────────────────────────

function StatusChip({ status }: { status: string }) {
  if (status === 'success')
    return <Chip icon={<CheckCircleIcon />}  label="OK"         color="success" size="small" />;
  if (status === 'failed')
    return <Chip icon={<ErrorIcon />}        label="Failed"     color="error"   size="small" />;
  if (status === 'no_change')
    return <Chip icon={<CheckCircleIcon />}  label="No Change"  color="default" size="small" />;
  return   <Chip icon={<HourglassEmptyIcon />} label="Never Run" color="default" size="small" variant="outlined" />;
}

// ── Main component ──────────────────────────────────────────────────────────────

export default function ThreatIntelPanel() {
  const [status,   setStatus]   = useState<Record<string, SourceStatus>>({});
  const [loading,  setLoading]  = useState(true);
  const [checking, setChecking] = useState(false);
  const [msg,      setMsg]      = useState('');
  const [msgSev,   setMsgSev]   = useState<'success' | 'error' | 'info'>('info');
  const [lastCheck, setLastCheck] = useState<Date | null>(null);

  async function fetchStatus() {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/v1/threat-intel/status`);
      setStatus(res.data ?? {});
      setLastCheck(new Date());
    } catch {
      flash('Failed to load threat intel status.', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchStatus(); }, []);

  function flash(text: string, sev: 'success' | 'error' | 'info' = 'info') {
    setMsg(text); setMsgSev(sev);
  }

  async function handleCheckNow() {
    setChecking(true);
    setMsg('');
    try {
      const res = await axios.post(`${API_BASE}/v1/threat-intel/check-now`);
      flash(res.data.message ?? 'Update complete.', 'success');
      await fetchStatus();
    } catch {
      flash('Update failed — check server logs.', 'error');
    } finally {
      setChecking(false);
    }
  }

  const rows = Object.values(status);
  const failedCount = rows.filter((r) => r.status === 'failed').length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShieldIcon color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={700}>Threat Intelligence</Typography>
          {failedCount > 0 && (
            <Chip label={`${failedCount} failed`} color="error" size="small" />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {lastCheck && (
            <Typography variant="caption" color="text.secondary">
              Updated: {lastCheck.toLocaleTimeString()}
            </Typography>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={checking ? <CircularProgress size={14} /> : <RefreshIcon />}
            onClick={handleCheckNow}
            disabled={checking}
          >
            {checking ? 'Fetching…' : 'Check Now'}
          </Button>
        </Box>
      </Box>

      {msg && (
        <Alert severity={msgSev} sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>
      )}

      {rows.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 5, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
          <HourglassEmptyIcon sx={{ color: 'text.disabled', fontSize: 36, mb: 1 }} />
          <Typography color="text.secondary" variant="body2">
            No data yet. Click "Check Now" to run the first fetch.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Source</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Version / Date</TableCell>
                <TableCell>Schedule</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sx={{ maxWidth: 260 }}>Summary</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const meta = SOURCE_META[row.source] ?? {
                  label: row.source, icon: <ShieldIcon fontSize="small" />, desc: '',
                };
                return (
                  <TableRow
                    key={row.source}
                    sx={{
                      bgcolor: row.status === 'failed'
                        ? 'rgba(239,35,60,0.04)'
                        : row.status === 'never_run'
                        ? 'rgba(255,255,255,0.01)'
                        : undefined,
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ color: 'text.secondary' }}>{meta.icon}</Box>
                        <Typography variant="caption" fontWeight={600}>{meta.label}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{meta.desc}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {row.version_or_date ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.schedule}
                        size="small"
                        variant="outlined"
                        color={row.schedule === 'daily' ? 'primary' : 'default'}
                        sx={{ fontSize: '10px' }}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="caption" color="text.secondary">
                        {row.last_updated
                          ? new Date(row.last_updated).toLocaleString()
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={row.status} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Tooltip title={row.summary}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          sx={{ display: 'block', maxWidth: 250 }}
                        >
                          {row.summary}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
